// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Trans } from 'react-i18next';
import {
  defineComponent,
  DonutChartContainer,
  KpiBoxes,
  PageContainer,
  SectionContainer,
  Tabs,
} from '@shared/ui-kit';
import { useTraderAllocation } from '../hooks/use-trader-allocation';
import { Toolbar } from '@shared/advisory/aggregation/components';
import { OverviewTemplate, OverviewStocks } from '@shared/ui-kit/features/overview';
import { useTranslation } from 'react-i18next';

export const TraderAllocation = defineComponent({
  displayName: 'TraderAllocation',
  useSetup: useTraderAllocation,
  View: ({
    kpis,
    periodPicker,
    assetAnalysis,
    currency,
    assetClass,
    vehicles,
    banks,
    isLoading,
    selectedCurrency,
  }) => {
    const { t } = useTranslation('common');
    return (
      <PageContainer className="!mx-0 !px-0" loading={isLoading}>
        <Tabs.Root
          defaultValue="overview"
          className="space-y-10"
          variant="underline"
        >
          <Tabs.List>
            <Tabs.Trigger value="overview">
              {t('common.overview', { ns: 'common' })}
            </Tabs.Trigger>

            <Tabs.Trigger value="fund" disabled={assetAnalysis.fund === null}>
              {t('common.fund', { ns: 'common' })}
            </Tabs.Trigger>

            <Tabs.Trigger value="etf" disabled={assetAnalysis.etf === null}>
              {t('common.etf', { ns: 'common' })}
            </Tabs.Trigger>

            <Tabs.Trigger value="stock" disabled={assetAnalysis.stock === null}>
              {t('common.stock', { ns: 'common' })}
            </Tabs.Trigger>
          </Tabs.List>

          <Toolbar
            controls={['period']}
            onPeriodChange={periodPicker.onPeriodChange}
            onDateChange={periodPicker.onDateChange}
            availablePeriods={['YTD', '6M', '1Y', '3Y', '5Y', 'MAX']}
            defaultHist={periodPicker.defaultHist}
            disabled={
              ![assetAnalysis.stock, assetAnalysis.etf, assetAnalysis.fund].some(
                Boolean,
              )
            }
          />

          <Tabs.Content value="overview">
            <div className="grid grid-cols-3 gap-6 pb-8">
              {kpis.map((vals, idx) => (
                <KpiBoxes key={idx} {...vals} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-5">
              <SectionContainer>
                <DonutChartContainer
                  name={<Trans i18nKey="ai-builder.bank-broker" ns="ai-builder" />}
                  exposure={banks.labels}
                  weight={banks.values}
                  testId="bank-broker-chart"
                />
              </SectionContainer>
              <SectionContainer>
                <DonutChartContainer
                  name={<Trans i18nKey="common.currency" ns="common" />}
                  exposure={currency.labels}
                  weight={currency.values}
                  testId="currency-chart"
                />
              </SectionContainer>
              <SectionContainer>
                <DonutChartContainer
                  name={<Trans i18nKey="glossary.asset-class" ns="glossary" />}
                  exposure={assetClass.labels}
                  weight={assetClass.values}
                  testId="asset-class-chart"
                />
              </SectionContainer>
              <SectionContainer>
                <DonutChartContainer
                  name={<Trans i18nKey="glossary.vehicles" ns="glossary" />}
                  exposure={vehicles.labels}
                  weight={vehicles.values}
                  testId="vehicles-chart"
                />
              </SectionContainer>
            </div>
          </Tabs.Content>

          <Tabs.Content value="fund">
            <OverviewTemplate analysis={assetAnalysis.fund!} />
          </Tabs.Content>

          <Tabs.Content value="etf">
            <OverviewTemplate analysis={assetAnalysis.etf!} />
          </Tabs.Content>

          <Tabs.Content value="stock">
            <OverviewStocks
              analysis={assetAnalysis.stock!}
              currency={selectedCurrency}
            />
          </Tabs.Content>
        </Tabs.Root>
      </PageContainer>
    );
  },
});
